import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FaHome,
  FaUsers,
  FaBox,
  FaBook,
  FaFileInvoice,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronLeft as FaChevron,
  FaPlus,
} from "react-icons/fa";
import logo from "../assets/LedgerHisabLogo.png";
import zerlakLogo from "../assets/zerlak_logo.jpg";
import "../style/Sidebar.css";

const menuGroups = [
  {
    title: "Home",
    icon: <FaHome />,
    path: "/dashboard",
    noSubmenu: true,
  },
  {
    title: "Challan",
    icon: <FaFileInvoice />,
    subItems: [
      { 
        label: "Challan Entry+", 
        isNestedDropdown: true,
        nestedItems: [
          { label: "Delivered Challan", path: "/dashboard/challan/delivered" },
          { label: "Received Challan", path: "/dashboard/challan/received" },
        ]
      },
      // { label: "Delivered Entry", path: "/dashboard/challan/delivered" },
      // { label: "Received Entry", path: "/dashboard/challan/received" },
      { label: "View Challan", path: "/dashboard/view-challan", noNested: true },
      
    ],
  },
  {
    title: "Customers",
    icon: <FaUsers />,
    subItems: [
      { label: "Create Customer", path: "/dashboard/customers/create" },
      { label: "View Customers", path: "/dashboard/customers/view" },
    ],
  },
  {
    title: "View Passbook",
    icon: <FaBook />,
    path: "/dashboard/passbook",
    noSubmenu: true,
  },
  {
    title: "Items & Goods",
    icon: <FaBox />,
    subItems: [
      { label: "Goods", path: "/dashboard/goods" },
      { label: "Cylinder Master", path: "/dashboard/cylinders" },
    ],
  },
];

const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState({});
  const [openNestedMenus, setOpenNestedMenus] = useState({});

  // Auto-open menu if a child route is active
  useEffect(() => {
    const newOpen = {};
    menuGroups.forEach((group) => {
      if (group.subItems) {
        const isChildActive = group.subItems.some((item) => {
          if (!item.path) return false; // Skip items without path (like nested dropdowns)
          return location.pathname === item.path ||
          (item.path.includes("?") && location.pathname + location.search === item.path);
        });
        if (isChildActive) {
          newOpen[group.title] = true;
        }
      }
    });
    setOpenMenus((prev) => ({ ...prev, ...newOpen }));
  }, [location.pathname, location.search]);

  const toggleMenu = (title) => {
    if (collapsed) {
      // If sidebar is collapsed and user clicks a dropdown, expand it first
      onToggle();
      // Then open the specific menu after a brief delay to allow the sidebar to expand
      setTimeout(() => {
        setOpenMenus((prev) => ({
          ...prev,
          [title]: true,
        }));
      }, 100);
      return;
    }
    setOpenMenus((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const toggleNestedMenu = (label) => {
    setOpenNestedMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const isGroupActive = (group) => {
    // Don't highlight parent items if they have submenu
    if (group.subItems) return false;
    
    if (group.path && location.pathname === group.path) return true;
    return false;
  };

  const handleMenuKeyPress = (title, e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMenu(title);
    }
  };

  const handleDirectNavigation = (path, title, e) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <aside
      className={`sidebar ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}
      aria-label="Main navigation"
      style={{
        position: 'fixed',
        top: 0,
        left: mobileOpen ? 0 : (window.innerWidth <= 768 ? '-100%' : '0'),
        width: window.innerWidth <= 768 ? '280px' : (collapsed ? '72px' : '240px'),
        height: '100vh',
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e2e8f0',
        transition: 'left 0.3s ease, width 0.3s ease',
        zIndex: 900,
        overflow: 'hidden',
        boxShadow: window.innerWidth <= 768 ? '2px 0 10px rgba(0,0,0,0.1)' : 'none',
        boxSizing: 'border-box'
      }}
    >
      {/* Header with logo + small arrow toggle */}
      <div 
        className="sidebar-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: collapsed ? '16px 8px' : '16px 16px',
          borderBottom: '1px solid #e2e8f0',
          minHeight: '70px',
          overflow: 'hidden'
        }}
      >
        <div 
          className="logo-wrapper"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: collapsed ? '0' : '12px',
            overflow: 'hidden'
          }}
        >
          <img 
            src={logo} 
            alt="Ledger Hisab Logo" 
            className="logo-main" 
            style={{
              width: collapsed ? '36px' : '40px',
              height: collapsed ? '36px' : '40px',
              flexShrink: 0
            }}
          />
          {!collapsed && (
            <span 
              className="logo-text"
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1e293b',
                whiteSpace: 'nowrap'
              }}
            >
              CTM
            </span>
          )}
        </div>

        {window.innerWidth > 768 && (
          <button
            className="sidebar-toggle-arrow"
            onClick={onToggle}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '8px',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            {collapsed ? <FaChevronRight size={18} /> : <FaChevronLeft size={18} />}
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav 
        className="sidebar-nav" 
        role="navigation"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: collapsed ? '8px 4px' : '12px 8px'
        }}
      >
        {menuGroups.map((group) => {
          const isOpen = openMenus[group.title] || false;
          const hasSubmenu = !group.noSubmenu && group.subItems?.length > 0;
          const active = isGroupActive(group);

          return (
            <div key={group.title} className="menu-group">
              <div
                className={`menu-item 
                  ${active ? "active" : ""} 
                  ${hasSubmenu ? "has-submenu" : ""} 
                  ${!hasSubmenu && active ? "leaf-active" : ""}`}
                onClick={(e) => {
                  if (hasSubmenu) {
                    toggleMenu(group.title);
                  } else if (group.path) {
                    handleDirectNavigation(group.path, group.title, e);
                  }
                }}
                onKeyPress={(e) => {
                  if (hasSubmenu) {
                    handleMenuKeyPress(group.title, e);
                  } else if (group.path && (e.key === 'Enter' || e.key === ' ')) {
                    handleDirectNavigation(group.path, group.title, e);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={hasSubmenu ? isOpen : undefined}
              >
                <div className="menu-icon" aria-hidden="true">
                  {group.icon}
                </div>

                {!collapsed && (
                  <>
                    <span className="menu-label">
                      {group.path ? (
                        <NavLink
                          to={group.path}
                          end
                          className={({ isActive }) =>
                            isActive && !hasSubmenu ? "active-leaf-link" : ""
                          }
                        >
                          {group.title}
                        </NavLink>
                      ) : (
                        group.title
                      )}
                    </span>

                    {hasSubmenu && (
                      <span className="menu-arrow" aria-hidden="true">
                        {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                    )}
                  </>
                )}
              </div>

              {hasSubmenu && isOpen && !collapsed && (
                <div className="submenu">
                  {group.subItems.map((item) => {
                    if (item.isNestedDropdown) {
                      const isNestedOpen = openNestedMenus[item.label] || false;
                      return (
                        <div key={item.label} className="nested-dropdown">
                          <div
                            className="nested-dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleNestedMenu(item.label);
                            }}
                          >
                            <span>{item.label}</span>
                            <FaChevronRight 
                              style={{
                                fontSize: '10px',
                                transition: 'transform 0.2s ease',
                                transform: isNestedOpen ? 'rotate(90deg)' : 'rotate(0deg)'
                              }}
                            />
                          </div>
                          {isNestedOpen && (
                            <div className="nested-submenu">
                              {item.nestedItems.map((nestedItem) => (
                                <NavLink
                                  key={nestedItem.label}
                                  to={nestedItem.path}
                                  className={({ isActive }) =>
                                    `nested-submenu-item ${isActive ? "active" : ""}`
                                  }
                                  end
                                >
                                  {nestedItem.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    return (
                      <NavLink
                        key={item.label}
                        to={item.path}
                        className={({ isActive }) =>
                          `submenu-item ${isActive ? "active" : ""}`
                        }
                        end
                      >
                        {item.label}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div 
        className="sidebar-footer"
        style={{
          padding: collapsed ? '8px 4px' : '16px 8px',
          borderTop: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}
      >
        {!collapsed && (
          <div 
            className="footer-content"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
              color: '#64748b'
            }}
          >
            <small>Design & Developed by</small>
            <img 
              src={zerlakLogo} 
              alt="Zerlak" 
              className="zerlak-logo" 
              style={{
                width: '60px',
                height: 'auto'
              }}
            />
            <small>v1.0.2</small>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;